import type { ConnectedContentInlineModel } from 'autogen/swagger/all';
import ContentCard from 'components/ContentCard';
import { mapResponsiveImageModelToImageProps } from 'components/generic/Image/helpers/mapToImageProps';
import { guardIsPageActionOfTypeLinkAction } from 'components/shared/Routing/components/shared/helpers';
import useAdformImpressionTracking from 'hooks/tracking/useAdformImpressionTracking';
import { useMemo, useRef } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import styled, { css } from 'styled-components';
import QuickViewArticle from 'components/QuickViewArticle/QuickViewArticle';
import { useQuickView } from 'components/quickView';
import { ReadMore } from 'autogen/translation-keys/trans-website-shared';
import { useTranslation } from 'react-i18next';
import { postAdformClickTracking } from 'modules/tracking/adformClickTracking';

const StyledContentCard = styled(ContentCard)<{ $spanTwo: boolean; $isPreview: boolean }>`
    min-height: 388px;

    ${({ $spanTwo }) =>
        $spanTwo &&
        css`
            grid-column: span 2;
        `};

    ${({ $isPreview }) =>
        $isPreview &&
        css`
            min-height: 425px;
            height: 1px;
        `};
`;

function InlineConnectedContent({ model }: { model: ConnectedContentInlineModel }) {
    const {
        image,
        link,
        heading,
        text,
        widthType,
        contentLabel,
        adformTracking,
        dataLayer,
        isQuickView,
        placement,
        isPreview,
        designThemeNameOverride
    } = model;
    const imageProps = useMemo(() => mapResponsiveImageModelToImageProps(image), [image]);
    const linkAction = guardIsPageActionOfTypeLinkAction(link?.pageAction);
    const linkProps = linkAction && !isQuickView ? { action: linkAction } : undefined;
    const tags = contentLabel ? [contentLabel] : undefined;
    const ref = useRef<HTMLImageElement | null>(null);
    const identifier = `placement-${placement}_size-${widthType}`;
    const { toggle } = useQuickView(identifier);
    const { t } = useTranslation();

    function handleOpenQuickView() {
        toggle(true);

        if (adformTracking?.clickTracking) {
            postAdformClickTracking(adformTracking.clickTracking);
        }
    }

    useAdformImpressionTracking({
        ref: ref,
        impTrackingUrls: adformTracking?.impTrackers
    });

    return (
        <>
            {linkAction && isQuickView && <QuickViewArticle quickViewId={identifier} linkAction={linkAction.action} />}

            <StyledContentCard
                ref={ref}
                $spanTwo={widthType === 'Two'}
                $isPreview={isPreview}
                image={{
                    ...imageProps,
                    aspectRatio: 'fill',
                    sizes: widthType === 'Two' ? '500px' : undefined
                }}
                link={linkProps}
                heading={text}
                subheading={heading}
                tags={tags}
                data-datalayer={JSON.stringify(dataLayer)}
                actions={
                    isQuickView ? [{ onClick: handleOpenQuickView, label: t(ReadMore), variant: 'outline' }] : undefined
                }
                type={designThemeNameOverride}
            />
        </>
    );
}

export default withErrorBoundary(InlineConnectedContent, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
